'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useEffect, useMemo, useRef } from 'react';
import { LoadingMorph } from '@extern/ui/loading-morph';
import dynamic from 'next/dynamic';
import { usePathname, useSearchParams } from 'next/navigation';
import { useAuthEvents } from '@/app/(auth)/_hooks/use-auth-events';
import { parseInterviewArrival } from '@/app/(auth)/sign-up/_utils/interview-arrival';
import { parseLabsArrival } from '@/app/(auth)/sign-up/_utils/labs-arrival';
import { parseMatcherArrival } from '@/app/(auth)/sign-up/_utils/matcher-arrival';
import { parsePortfolioArrival } from '@/app/(auth)/sign-up/_utils/portfolio-arrival';
import { CenteredAuthLayout } from './centered-auth-layout';
import { MatcherCatalogPrefetch } from './matcher-arrival/matcher-catalog-prefetch';

/**
 * Loaded only when the URL says the visitor just claimed a portfolio, so the panel and its lookup
 * never reach the bundle of an ordinary sign-up or sign-in load.
 */
const PortfolioArrivalLayout = dynamic(() => import('./portfolio-arrival/portfolio-arrival-layout').then(mod => mod.PortfolioArrivalLayout), {
  loading: () => <AuthLoading />,
  ssr: false
});

/**
 * The Labs arrival layout is loaded only when a URL actually carries a lab result, so the packs it
 * renders from never reach the bundle of an ordinary sign-up or sign-in load.
 */
const LabsArrivalLayout = dynamic(() => import('./labs-arrival/labs-arrival-layout').then(mod => mod.LabsArrivalLayout), {
  loading: () => <AuthLoading />,
  ssr: false
});

/** The practice-interview arrival, loaded only when a URL carries a scorecard, for the same reason. */
const InterviewArrivalLayout = dynamic(() => import('./interview-arrival/interview-arrival-layout').then(mod => mod.InterviewArrivalLayout), {
  loading: () => <AuthLoading />,
  ssr: false
});

/**
 * Loaded only when the URL carries the homepage matcher's picks, so the panel, its program table and the
 * catalog lookup never reach the bundle of an ordinary sign-up or sign-in load.
 */
const MatcherArrivalLayout = dynamic(() => import('./matcher-arrival/matcher-arrival-layout').then(mod => mod.MatcherArrivalLayout), {
  loading: () => <AuthLoading />,
  ssr: false
});
function AuthLoading() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex min-h-screen w-full flex-col items-center justify-center bg-role-surface"><LoadingMorph /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/**
 * What `auth_screen_viewed` carries for the arrivals that get their own layout. They are told apart by
 * parameters that never co-occur; the order here matches the render order below.
 */
function arrivalEventProps(portfolioArrival: ReturnType<typeof parsePortfolioArrival>, labsArrival: ReturnType<typeof parseLabsArrival>, interviewArrival: ReturnType<typeof parseInterviewArrival>, matcherArrival: ReturnType<typeof parseMatcherArrival>): Record<string, unknown> | undefined {
  if (portfolioArrival) {
    return {
      portfolio_arm: portfolioArrival.arm,
      portfolio_handoff: true
    };
  }
  if (labsArrival) {
    return {
      labs_archetype: labsArrival.archetypeId,
      labs_rail: true
    };
  }
  if (interviewArrival) {
    return {
      interview_rail: true,
      interview_rounds: interviewArrival.levels.join('.')
    };
  }
  if (matcherArrival) {
    return {
      matcher_focus: matcherArrival.focus.join('.'),
      matcher_goal: matcherArrival.goal.join('.'),
      matcher_handoff: true,
      matcher_picks: matcherArrival.picks.join('.'),
      matcher_program: matcherArrival.program,
      matcher_stage: matcherArrival.stage
    };
  }
  return;
}
interface AuthLayoutSwitcherProps {
  children: ReactNode;
}
export function AuthLayoutSwitcher(t0) {
  const $ = _c(36);
  const {
    children
  } = t0;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    trackAuthScreenViewed
  } = useAuthEvents();
  const lastTrackedPathRef = useRef(null);
  let t1;
  if ($[0] !== pathname || $[1] !== searchParams) {
    t1 = pathname?.includes("/sign-up") ? parsePortfolioArrival(searchParams) : null;
    $[0] = pathname;
    $[1] = searchParams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const portfolioArrival = t1;
  let t2;
  if ($[3] !== pathname || $[4] !== searchParams) {
    t2 = pathname?.includes("/sign-up") ? parseLabsArrival(searchParams) : null;
    $[3] = pathname;
    $[4] = searchParams;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const labsArrival = t2;
  let t3;
  if ($[6] !== pathname || $[7] !== searchParams) {
    t3 = pathname?.includes("/sign-up") ? parseMatcherArrival(searchParams) : null;
    $[6] = pathname;
    $[7] = searchParams;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const matcherArrival = t3;
  let t4;
  if ($[9] !== pathname || $[10] !== searchParams) {
    t4 = pathname?.includes("/sign-up") ? parseInterviewArrival(searchParams) : null;
    $[9] = pathname;
    $[10] = searchParams;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const interviewArrival = t4;
  let t5;
  let t6;
  if ($[12] !== interviewArrival || $[13] !== labsArrival || $[14] !== matcherArrival || $[15] !== pathname || $[16] !== portfolioArrival || $[17] !== trackAuthScreenViewed) {
    t5 = () => {
      if (!pathname) {
        return;
      }
      if (lastTrackedPathRef.current === pathname) {
        return;
      }
      if (pathname.includes("/sign-up")) {
        trackAuthScreenViewed("sign_up", "entry", "start", arrivalEventProps(portfolioArrival, labsArrival, interviewArrival, matcherArrival));
        lastTrackedPathRef.current = pathname;
        return;
      }
      if (pathname.includes("/sign-in")) {
        trackAuthScreenViewed("sign_in", "entry", "start");
        lastTrackedPathRef.current = pathname;
      }
    };
    t6 = [interviewArrival, labsArrival, matcherArrival, pathname, portfolioArrival, trackAuthScreenViewed];
    $[12] = interviewArrival;
    $[13] = labsArrival;
    $[14] = matcherArrival;
    $[15] = pathname;
    $[16] = portfolioArrival;
    $[17] = trackAuthScreenViewed;
    $[18] = t5;
    $[19] = t6;
  } else {
    t5 = $[18];
    t6 = $[19];
  }
  useEffect(t5, t6);
  if (portfolioArrival) {
    let t7;
    if ($[20] !== children || $[21] !== portfolioArrival) {
      t7 = <PortfolioArrivalLayout arrival={portfolioArrival}>{children}</PortfolioArrivalLayout>;
      $[20] = children;
      $[21] = portfolioArrival;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    return t7;
  }
  let t7;
  if ($[23] !== children) {
    t7 = <CenteredAuthLayout>{children}</CenteredAuthLayout>;
    $[23] = children;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  const shipped = t7;
  if (labsArrival) {
    let t8;
    if ($[25] !== children || $[26] !== labsArrival || $[27] !== shipped) {
      t8 = <LabsArrivalLayout fallback={shipped} params={labsArrival}>{children}</LabsArrivalLayout>;
      $[25] = children;
      $[26] = labsArrival;
      $[27] = shipped;
      $[28] = t8;
    } else {
      t8 = $[28];
    }
    return t8;
  }
  if (interviewArrival) {
    let t8;
    if ($[29] !== children || $[30] !== interviewArrival) {
      t8 = <InterviewArrivalLayout params={interviewArrival}>{children}</InterviewArrivalLayout>;
      $[29] = children;
      $[30] = interviewArrival;
      $[31] = t8;
    } else {
      t8 = $[31];
    }
    return t8;
  }
  if (matcherArrival) {
    let t8;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <MatcherCatalogPrefetch />;
      $[32] = t8;
    } else {
      t8 = $[32];
    }
    let t9;
    if ($[33] !== children || $[34] !== matcherArrival) {
      t9 = <>{t8}<MatcherArrivalLayout arrival={matcherArrival}>{children}</MatcherArrivalLayout></>;
      $[33] = children;
      $[34] = matcherArrival;
      $[35] = t9;
    } else {
      t9 = $[35];
    }
    return t9;
  }
  return shipped;
}